/**
 * Append the active outlet to an internal /supply href so navigation never
 * drops the user's outlet context. Single-outlet users survive a dropped
 * param (resolve-outlet auto-lands them) but HQ/multi-outlet users get
 * bounced to the picker on every tab/tile tap without it — the cu go-live
 * walkthrough bug, inherited here wholesale.
 *
 * Not the only mechanism, and no longer the last line of defence: a link that
 * goes out bare (a loading.tsx has no session; the outlet-agnostic HQ desk
 * pages pass null) is resolved server-side from the persisted outlet cookie —
 * see ./outlet-server.ts. What this still buys is an EXPLICIT outlet in the
 * URL: shareable, bookmarkable, and unambiguous in a bug report.
 *
 * Inserts the param BEFORE a fragment, not after it: `outlet=` has to land
 * in the query string, which the server reads, not inside the `#fragment`,
 * which never leaves the browser. Appending unconditionally at the end would
 * turn `/x#s` into `/x#s?outlet=MET` — a fragment with a literal `?` in it,
 * which the server never sees. No nav href in this app carries a hash today,
 * so this is latent, not observed.
 */
export function withOutlet(
  href: string,
  outletCode: string | null | undefined,
): string {
  if (!outletCode) return href;
  const hashIndex = href.indexOf("#");
  const hash = hashIndex === -1 ? "" : href.slice(hashIndex);
  const base = hashIndex === -1 ? href : href.slice(0, hashIndex);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}outlet=${encodeURIComponent(outletCode)}${hash}`;
}
